import { adaptTraceStepsFromAgentEvents, adaptTraceSummary } from './assistantTrace.adapter'
import { createLocalId, asArray, asRecord, pickFirstString, parseResponseWrapper } from '@/utils/AIGuidance'

function detectContentType(content) {
  return /```|#{1,6}\s|\*\*|(^|\n)\s*(?:-\s+|\d+\.\s+)/.test(content) ? 'markdown' : 'text'
}

function normalizeTimestamp(value) {
  if (typeof value === 'number') {
    return new Date(value * (value < 1e12 ? 1000 : 1)).toISOString()
  }

  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value)
    if (!Number.isNaN(numeric)) {
      return new Date(numeric * (numeric < 1e12 ? 1000 : 1)).toISOString()
    }

    const parsed = new Date(value)
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString()
    }
  }

  return new Date().toISOString()
}

function createMessage(id, conversationId, role, content, createdAt, raw) {
  const record = asRecord(raw)
  const parsedResponse = role === 'assistant' ? parseResponseWrapper(content) : null
  // no_match 为通用提示卡片类型：message 由卡片展示，正文不再重复输出
  const isNoMatch = Boolean(parsedResponse && parsedResponse.type === 'no_match')
  const finalContent = parsedResponse
    ? (isNoMatch ? '' : parsedResponse.message)
    : content
  const finalRaw = parsedResponse
    ? {
        ...record,
        suggested_questions: parsedResponse.options,
        doctors: parsedResponse.doctors,
        packages: parsedResponse.packages,
        next_actions: parsedResponse.next_actions,
        responseType: parsedResponse.type,
        ...(isNoMatch ? { responseMessage: parsedResponse.message } : {}),
      }
    : record
  return {
    id,
    conversationId,
    role,
    content: finalContent,
    contentType: detectContentType(finalContent),
    createdAt: normalizeTimestamp(createdAt),
    status: 'sent',
    traceSteps: role === 'assistant' ? adaptTraceStepsFromAgentEvents(record.agent_events) : [],
    traceSummary: role === 'assistant' ? adaptTraceSummary(record) : '',
    raw: finalRaw,
  }
}

function expandRecord(record, index, fallbackConversationId) {
  const conversationId = record.conversation_id || fallbackConversationId
  const messages = []
  const userMessage = typeof record.user_message === 'string' ? record.user_message.trim() : ''
  const assistantMessage = typeof record.assistant_message === 'string' ? record.assistant_message.trim() : ''
  const baseMessageId = record.message_id || createLocalId(`msg-${index}`)
  const userMessageId = baseMessageId
  const assistantMessageId = typeof record.assistant_message_id === 'string' && record.assistant_message_id && record.assistant_message_id !== userMessageId
      ? record.assistant_message_id
      : `${baseMessageId}-assistant`

  if (userMessage) {
    messages.push(createMessage(userMessageId, conversationId, 'user', userMessage, record.created_at, record))
  }

  if (assistantMessage) {
    messages.push(createMessage(assistantMessageId, conversationId, 'assistant', assistantMessage, record.updated_at ?? record.created_at, record))
  }

  if (messages.length === 0) {
    const fallbackContent = pickFirstString(record, ['content', 'text', 'message'])
    if (fallbackContent) {
      messages.push(createMessage(baseMessageId, conversationId, 'assistant', fallbackContent, record.created_at ?? record.updated_at, record))
    }
  }

  return messages
}

export function adaptConversationMessagesResponse(rawResponse, conversationId) {
  const response = asRecord(rawResponse)
  const data = asRecord(response.data)
  const list = asArray(data.items).length > 0 ? asArray(data.items) : asArray(response.items)

  const items = list
    .flatMap((item, index) => expandRecord(asRecord(item), index, conversationId))
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())

  const pageNum = Number(data.page_num ?? response.page_num ?? 1) || 1
  const pageSize = Number(data.page_size ?? response.page_size ?? (items.length || 10)) || 10
  const totalCount = Number(data.total_count ?? response.total_count ?? items.length) || items.length
  const totalPage = Number(data.total_page ?? response.total_page ?? 1) || 1
  const hasMore = pageNum < totalPage

  return {
    items,
    hasMore,
    nextPage: hasMore ? pageNum + 1 : undefined,
    pageNum,
    pageSize,
    totalCount,
    totalPage,
  }
}
